// Push Notifications utility for PWA

const NOTIFICATION_PERMISSION_KEY = 'destiny_notification_permission';
const CLOCK_IN_REMINDER_KEY = 'destiny_clock_in_reminder';
const CLOCK_OUT_REMINDER_KEY = 'destiny_clock_out_reminder';
const API_URL = process.env.REACT_APP_BACKEND_URL;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const arr = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) arr[i] = rawData.charCodeAt(i);
  return arr;
}

export const isNotificationSupported = () => {
  return 'Notification' in window && 'serviceWorker' in navigator;
};

export const isPushSupported = () => {
  return isNotificationSupported() && 'PushManager' in window;
};

export const getNotificationPermission = () => {
  if (!isNotificationSupported()) return 'unsupported';
  return Notification.permission;
};

export const requestNotificationPermission = async () => {
  if (!isNotificationSupported()) {
    throw new Error('Notifications not supported');
  }

  const permission = await Notification.requestPermission();
  localStorage.setItem(NOTIFICATION_PERMISSION_KEY, permission);
  return permission;
};

export const subscribeToPush = async () => {
  if (!isPushSupported()) return null;
  
  // Get VAPID key from env or fetch from backend
  let vapidKey = VAPID_PUBLIC_KEY;
  if (!vapidKey) {
    try {
      const resp = await fetch(`${API_URL}/api/push/vapid-key`);
      const data = await resp.json();
      vapidKey = data.public_key;
    } catch (e) {
      console.error('Failed to fetch VAPID key:', e);
      return null;
    }
  }
  if (!vapidKey) return null;

  const permission = await requestNotificationPermission();
  if (permission !== 'granted') return null;

  try {
    const registration = await navigator.serviceWorker.register('/sw-push.js');
    await navigator.serviceWorker.ready;

    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });
    }

    const token = localStorage.getItem('session_token');
    if (token) {
      await fetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ endpoint: subscription.endpoint, keys: subscription.toJSON().keys })
      });
    }
    localStorage.setItem('destiny_push_subscribed', 'true');
    return subscription;
  } catch (e) {
    console.error('Push subscribe error:', e);
    return null;
  }
};

export const unsubscribeFromPush = async () => {
  try {
    const registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
    if (registration) {
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) await subscription.unsubscribe();
    }
    const token = localStorage.getItem('session_token');
    if (token) {
      await fetch(`${API_URL}/api/push/unsubscribe`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    }
    localStorage.removeItem('destiny_push_subscribed');
  } catch (e) {
    console.error('Push unsubscribe error:', e);
  }
};

export const isPushSubscribed = () => {
  return localStorage.getItem('destiny_push_subscribed') === 'true';
};

export const showNotification = (title, options = {}) => {
  if (getNotificationPermission() !== 'granted') {
    console.log('Notification permission not granted');
    return;
  }

  const defaultOptions = {
    icon: '/logo192.png',
    badge: '/logo192.png',
    vibrate: [200, 100, 200],
    tag: 'destiny-notification',
    requireInteraction: false,
    ...options
  };

  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.ready.then(registration => {
      registration.showNotification(title, defaultOptions);
    });
  } else {
    new Notification(title, defaultOptions);
  }
};

export const scheduleClockInReminder = (time = '08:30') => {
  localStorage.setItem(CLOCK_IN_REMINDER_KEY, time);
  
  const [hours, minutes] = time.split(':').map(Number);
  const now = new Date();
  const reminderTime = new Date();
  reminderTime.setHours(hours, minutes, 0, 0);
  
  if (reminderTime <= now) {
    reminderTime.setDate(reminderTime.getDate() + 1);
  }
  
  const delay = reminderTime.getTime() - now.getTime();
  
  setTimeout(() => {
    showNotification('Peringatan Clock In', {
      body: 'Jangan lupa untuk clock in hari ini!',
      tag: 'clock-in-reminder',
      requireInteraction: true,
      actions: [
        { action: 'clock-in', title: 'Clock In Sekarang' },
        { action: 'dismiss', title: 'Abaikan' }
      ]
    });
    scheduleClockInReminder(time);
  }, delay);
};

export const scheduleClockOutReminder = (time = '17:30') => {
  localStorage.setItem(CLOCK_OUT_REMINDER_KEY, time);
  
  const [hours, minutes] = time.split(':').map(Number);
  const now = new Date();
  const reminderTime = new Date();
  reminderTime.setHours(hours, minutes, 0, 0);
  
  if (reminderTime <= now) {
    reminderTime.setDate(reminderTime.getDate() + 1);
  }
  
  const delay = reminderTime.getTime() - now.getTime();
  
  setTimeout(() => {
    showNotification('Peringatan Clock Out', {
      body: 'Sudah hampir waktu pulang. Jangan lupa clock out!',
      tag: 'clock-out-reminder',
      requireInteraction: true,
      actions: [
        { action: 'clock-out', title: 'Clock Out Sekarang' },
        { action: 'dismiss', title: 'Abaikan' }
      ]
    });
    scheduleClockOutReminder(time);
  }, delay);
};

export const notifyLeaveApproval = (staffName, status, leaveType) => {
  const statusText = status === 'approved' ? 'DILULUSKAN' : 'DITOLAK';
  const body = `Permohonan cuti ${leaveType} anda telah ${statusText}.`;
  
  showNotification(`Cuti ${statusText}`, {
    body,
    tag: 'leave-notification',
    requireInteraction: true
  });
};

export const getClockInReminderTime = () => {
  return localStorage.getItem(CLOCK_IN_REMINDER_KEY) || '08:30';
};

export const getClockOutReminderTime = () => {
  return localStorage.getItem(CLOCK_OUT_REMINDER_KEY) || '17:30';
};

export const cancelAllReminders = () => {
  localStorage.removeItem(CLOCK_IN_REMINDER_KEY);
  localStorage.removeItem(CLOCK_OUT_REMINDER_KEY);
};
